import React, { useEffect, useRef, useState, useCallback } from 'react';
import Page from '../../../components/page/Page';
import { useParams } from 'react-router-dom';
import { useCompanyCore } from '../../../../core/modules/company';
import useThemeMediaQuery from '../../../../core/hooks/useThemeMediaQuery';
import { motion } from 'framer-motion';
import OverviewWidget from '../../../components/companyMetrics/OverviewWidget';
import ProductsWidget from '../../../components/companyMetrics/ProductsWidget';
import { Accordion, AccordionSummary, Alert, FormControl, MenuItem, Paper, Select, AccordionDetails, Typography, InputLabel, Tabs, Box, Tab, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Dialog, DialogTitle, DialogContent, IconButton, TextField, Collapse } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { useMetricsCore } from '../../../../core/modules/metrics';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import 'moment/locale/es';
import TotalWidget from '../../../components/companyMetrics/TotalWidget';
import AlertEvolutionWidget from '../../../components/companyMetrics/AlertEvolutionWidget';
import DataWidget from '../../../components/companyMetrics/DataWidget';
// import CounterWidget from '../../../components/companyMetrics/CounterWidget'; // COMENTADO - Usar StockAlertsWidget en su lugar
import StockAlertsWidget from '../../../components/StockAlertsWidget'; // ✅ COMPONENTE CORRECTO
import { useMeCore } from '../../../../core/modules/me';
import Spinner from '../../../components/Spinner';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { TabPanelProps } from '@chakra-ui/react';
import Tabs_JL from '../../../components/JL/Tabs_JL';
import apiService from '../../../../core/services/api-client';
import moment from 'moment';

function a11yProps(index: number) {
  return {
    id: `simple-tab-${index}`,
    'aria-controls': `simple-tabpanel-${index}`,
  };
}

interface PanelProps extends TabPanelProps {
  value: number;
  index: number;
}

function CustomTabPanel(props: PanelProps) {
  const { children, value, index, color, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && <Box>{children}</Box>}
    </div>
  );
}

const CompanyMetrics = () => {
  const { id } = useParams();
  const { user } = useMeCore();
  const { company, stores, getCompany, getStores, getMeCompany } = useCompanyCore();
  const {
    dateInit,
    dateEnd,
    store,
    overview,
    counters,
    products,
    setInitDate,
    setEndDate,
    setStore,
    getMetrics,
    clearMetrics,
  } = useMetricsCore();

  const pageLayout = useRef(null);
  const detailsRef = useRef<HTMLDivElement | null>(null);
  const isMobile = useThemeMediaQuery((theme) => theme.breakpoints.down('lg'));
  const [loading, setLoading] = useState(false);
  const [value, setValue] = useState(0);

  // Recurrencias
  const [recData, setRecData] = useState<any>(null);
  const [recLoading, setRecLoading] = useState(false);
  const [recDetailOpen, setRecDetailOpen] = useState(false);
  const [recDetailData, setRecDetailData] = useState<any>(null);
  const [recDetailLoading, setRecDetailLoading] = useState(false);
  const [recDetailSearch, setRecDetailSearch] = useState('');
  const [expandedRows, setExpandedRows] = useState<Record<string, boolean>>({});
  const [recFullListOpen, setRecFullListOpen] = useState(false);
  const [recFullListSearch, setRecFullListSearch] = useState('');
  const [recFullListAsc, setRecFullListAsc] = useState(false);

  useEffect(() => {
    if (id) {
      clearMetrics();
      if (user && user.is_superuser) {
        getCompany(id);
        getStores(id);
      } else {
        getMeCompany(id);
      }
    }
  }, [clearMetrics, getCompany, getMeCompany, getMetrics, getStores, id, user]);

  useEffect(() => {
    if (company && dateInit && dateEnd) {
      setLoading(true);
      getMetrics(
        company.id, {
        dateInit: dateInit.format('YYYY-MM-DD'),
        dateEnd: dateEnd.format('YYYY-MM-DD'),
        store: store === 'todos' ? '' : store,
      },
        () => {
          setLoading(false);
        },
        () => {
          setLoading(false);
        }
      );
    }
  }, [company, dateEnd, dateInit, store, getMetrics]);

  // Fetch recurrencias cuando se selecciona el tab
  useEffect(() => {
    if (value === 1 && id && !recData) {
      setRecLoading(true);
      const api = apiService();
      api.get(`/reports/${id}/recurrences`)
        .then((res) => setRecData(res.data))
        .catch(() => {})
        .finally(() => setRecLoading(false));
    }
  }, [value, id, recData]);

  const openRecDetail = useCallback(async (metricKey: string) => {
    if (!id) return;
    setRecDetailOpen(true);
    setRecDetailLoading(true);
    setRecDetailSearch('');
    setExpandedRows({});
    try {
      const api = apiService();
      const res = await api.get(`/reports/${id}/recurrences/${metricKey}/detail`);
      setRecDetailData(res.data);
    } catch {
      setRecDetailData(null);
    } finally {
      setRecDetailLoading(false);
    }
  }, [id]);

  const fmtDate = (d: string) => moment(d).format('DD/MM/YYYY');

  const container = {
    show: {
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  const item = {
    hidden: { opacity: 0, y: 20 },
    show: { opacity: 1, y: 0 },
  };

  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    setValue(newValue);
  };

  return (
    <Page
      content={
        <div className="w-full">
          <Paper className="flex items-center flex-wrap gap-16 p-16 sm:p-24 m-16 sm:mx-24 sm:mt-24 sm:mb-4">
            <div className='flex'>
              <LocalizationProvider dateAdapter={AdapterMoment} adapterLocale="es">
                <DatePicker
                  label="Inicio"
                  className="min-w-[120px]"
                  format="DD/MM/YYYY"
                  views={['year', 'month', 'day']}
                  value={dateInit}
                  onChange={setInitDate}
                />
              </LocalizationProvider>
              <LocalizationProvider dateAdapter={AdapterMoment} adapterLocale="es">
                <DatePicker
                  label="Fin"
                  className="min-w-[120px]"
                  format="DD/MM/YYYY"
                  views={['year', 'month', 'day']}
                  value={dateEnd}
                  onChange={setEndDate}
                />
              </LocalizationProvider>
            </div>
            {
              stores &&
              <FormControl className="flex min-w-[120px]" variant="outlined">
                <InputLabel id="store-label">Almacén</InputLabel>
                <Select
                  labelId="store-label"
                  label="Almacén"
                  value={store}
                  onChange={(ev) => setStore(ev.target.value)}
                  defaultValue=""
                >
                  <MenuItem value="todos">Todos</MenuItem>
                  {stores.map((store) => (
                    <MenuItem value={`${store.name}`} key={store.id}>
                      {store.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            }
            <Box sx={{ borderBottom: 1, borderColor: 'divider', marginLeft: 2 }}>
              <Tabs value={value} onChange={handleChange} aria-label="basic tabs example">
                <Tab label="Dashboard" {...a11yProps(0)} />
                <Tab label="Recurrencias" {...a11yProps(1)} />
              </Tabs>
            </Box>
            {loading && (
              <div className="m-24 flex">
                <Spinner show={loading} color="#000" />
                <span className="ml-6">Buscando métricas</span>
              </div>
            )}
          </Paper>

          <>
            <CustomTabPanel value={value} index={0}>
              <motion.div
                className="px-24 pt-0"
                variants={container}
                initial="hidden"
                animate="show"
              >

                {/* Resumen de Alertas Diarias + Estadísticas (sin Accordions) */}
                <StockAlertsWidget companyId={id || ""} />

                {/* Estadísticas - gráficos debajo de los cuadros */}
                <Box sx={{ backgroundColor: '#f5f5f5', borderRadius: '16px', p: 2, mt: -0.5 }}>
                  <Typography variant="h6" gutterBottom sx={{ mb: 1 }}>
                    Estadísticas 📈
                  </Typography>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-16">
                    <AlertEvolutionWidget companyId={id || ""} />
                    <TotalWidget />
                  </div>
                </Box>

              </motion.div>
              {/* COMENTADO - Alertas en Escalamiento oculto temporalmente por decisión del equipo (19/03/2026)
              <motion.div
                className="px-24 my-24"
                variants={container}
                initial="hidden"
                animate="show"
              >
                <Accordion className="w-full" defaultExpanded>
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    aria-controls="panel1-content"
                    id="panel1-header"
                  >
                    <Typography component="span" className="font-semibold text-xl">Alertas en Escalamiento 🕵🏻‍♂️</Typography>
                  </AccordionSummary>
                  <AccordionDetails className="grid grid-cols-4 gap-24 w-full min-w-0 p-24">
                    {products.map((metric, index) => (
                      <motion.div variants={item} key={index} className="col-span-4 lg:col-span-2">
                        <ProductsWidget metric={metric} isMobile={isMobile} />
                      </motion.div>
                    ))}
                  </AccordionDetails>
                </Accordion>
              </motion.div>
              */}
            </CustomTabPanel>
            <CustomTabPanel value={value} index={1}>
              <div className="px-24 my-24">
                {recLoading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                    <Spinner show={true} color="#000" />
                    <span className="ml-8">Cargando recurrencias...</span>
                  </Box>
                ) : recData ? (
                  <>
                    {/* Tabla resumen */}
                    <Paper className="shadow rounded-2xl mb-24 p-20">
                      <Typography sx={{ fontSize: 22, fontWeight: 700, mb: 0.5 }}>🔄 Recurrencias de Alertas</Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>Productos que entran y salen de alertas repetidamente</Typography>
                      <TableContainer>
                        <Table size="medium">
                          <TableHead>
                            <TableRow>
                              <TableCell sx={{ fontWeight: 700, borderBottom: 2, borderColor: 'divider' }}>Métrica</TableCell>
                              <TableCell sx={{ fontWeight: 700, borderBottom: 2, borderColor: 'divider' }}>Detalle</TableCell>
                              <TableCell align="right" sx={{ fontWeight: 700, borderBottom: 2, borderColor: 'divider' }}>Productos</TableCell>
                              <TableCell align="right" sx={{ fontWeight: 700, borderBottom: 2, borderColor: 'divider' }}>Prom. veces</TableCell>
                              <TableCell align="right" sx={{ fontWeight: 700, borderBottom: 2, borderColor: 'divider' }}>Máx. veces</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {(recData.summary || []).map((s: any, idx: number) => (
                              <TableRow key={s.metric_key} hover sx={{ bgcolor: idx % 2 === 0 ? 'transparent' : 'action.hover' }}>
                                <TableCell sx={{ fontWeight: 600, py: 1.5 }}>{s.metric}</TableCell>
                                <TableCell sx={{ py: 1.5 }}>
                                  <Typography
                                    sx={{ fontSize: 13, fontWeight: 600, color: 'primary.main', cursor: 'pointer', whiteSpace: 'nowrap', '&:hover': { textDecoration: 'underline' } }}
                                    onClick={() => openRecDetail(s.metric_key)}
                                  >
                                    Ver detalle →
                                  </Typography>
                                </TableCell>
                                <TableCell align="right" sx={{ fontWeight: 600, py: 1.5 }}>{s.products_with_recurrence?.toLocaleString('es-CL')}</TableCell>
                                <TableCell align="right" sx={{ py: 1.5 }}>{s.avg_times?.toFixed(1)}</TableCell>
                                <TableCell align="right" sx={{ fontWeight: 700, color: '#f44336', py: 1.5 }}>{s.max_times}</TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </Paper>

                    {/* Top 20 productos recurrentes */}
                    <Paper className="shadow rounded-2xl mb-24 p-20" sx={{ overflowX: 'auto', '&::-webkit-scrollbar': { height: 8 }, '&::-webkit-scrollbar-track': { background: '#e0e0e0', borderRadius: 4 }, '&::-webkit-scrollbar-thumb': { background: '#333', borderRadius: 4 }, scrollbarWidth: 'thin', scrollbarColor: '#333 #e0e0e0' }}>
                      <Typography sx={{ fontSize: 20, fontWeight: 700, mb: 0.5 }}>🏆 Top Productos Más Recurrentes</Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>Productos que más veces han entrado en alerta</Typography>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell sx={{ fontWeight: 700, whiteSpace: 'nowrap', borderBottom: 2, borderColor: 'divider' }}>SKU</TableCell>
                            <TableCell sx={{ fontWeight: 700, borderBottom: 2, borderColor: 'divider' }}>Producto</TableCell>
                            <TableCell sx={{ fontWeight: 700, whiteSpace: 'nowrap', borderBottom: 2, borderColor: 'divider' }}>Métrica</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 700, whiteSpace: 'nowrap', borderBottom: 2, borderColor: 'divider' }}>Veces</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 700, whiteSpace: 'nowrap', borderBottom: 2, borderColor: 'divider' }}>Primera vez</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 700, whiteSpace: 'nowrap', borderBottom: 2, borderColor: 'divider' }}>Última vez</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 700, whiteSpace: 'nowrap', borderBottom: 2, borderColor: 'divider' }}>Prom. días</TableCell>
                            <TableCell sx={{ fontWeight: 700, whiteSpace: 'nowrap', borderBottom: 2, borderColor: 'divider' }}>Proveedor</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {(recData.products || []).slice(0, 5).map((p: any, idx: number) => (
                            <TableRow key={p.sku + p.metric_key} hover sx={{ bgcolor: idx % 2 === 0 ? 'transparent' : 'action.hover' }}>
                              <TableCell sx={{ whiteSpace: 'nowrap' }}>{p.sku}</TableCell>
                              <TableCell sx={{ maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.name}</TableCell>
                              <TableCell sx={{ whiteSpace: 'nowrap' }}>{p.metric}</TableCell>
                              <TableCell align="right" sx={{ fontWeight: 700, color: '#f44336' }}>{p.occurrences}</TableCell>
                              <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>{fmtDate(p.first_occurrence)}</TableCell>
                              <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>{fmtDate(p.last_occurrence)}</TableCell>
                              <TableCell align="right">{p.avg_days_in_alert?.toFixed(1)}</TableCell>
                              <TableCell sx={{ whiteSpace: 'nowrap' }}>{p.supplier || '-'}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                      <Box sx={{ pt: 2, textAlign: 'center', borderTop: '1px solid', borderColor: 'divider', mt: 1 }}>
                        <Typography
                          sx={{ fontSize: 14, fontWeight: 600, color: 'primary.main', cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
                          onClick={() => { setRecFullListOpen(true); setRecFullListSearch(''); setRecFullListAsc(false); }}
                        >
                          Ver lista completa →
                        </Typography>
                      </Box>
                    </Paper>
                  </>
                ) : (
                  <Paper className="p-24 text-center shadow rounded-2xl">
                    <Typography color="text.secondary">No hay datos de recurrencias disponibles</Typography>
                  </Paper>
                )}
              </div>

              {/* Modal detalle recurrencias */}
              <Dialog open={recDetailOpen} onClose={() => setRecDetailOpen(false)} maxWidth="md" fullWidth>
                <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Box>
                    <Typography sx={{ fontWeight: 700, fontSize: 18 }}>
                      {recDetailData?.metric || ''} - Recurrencias
                    </Typography>
                    {recDetailData && (
                      <Typography variant="caption" color="text.secondary">
                        {recDetailData.total?.toLocaleString('es-CL')} productos
                      </Typography>
                    )}
                  </Box>
                  <IconButton onClick={() => setRecDetailOpen(false)} size="small"><CloseIcon /></IconButton>
                </DialogTitle>
                <DialogContent>
                  {recDetailLoading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                      <Spinner show={true} color="#000" />
                    </Box>
                  ) : recDetailData ? (
                    <>
                      <TextField
                        size="small"
                        placeholder="Buscar por SKU o nombre..."
                        fullWidth
                        sx={{ mb: 2 }}
                        value={recDetailSearch}
                        onChange={(e) => setRecDetailSearch(e.target.value)}
                      />
                      <TableContainer sx={{ maxHeight: 500 }}>
                        <Table size="small" stickyHeader>
                          <TableHead>
                            <TableRow>
                              <TableCell sx={{ fontWeight: 700, width: 40 }} />
                              <TableCell sx={{ fontWeight: 700 }}>SKU</TableCell>
                              <TableCell sx={{ fontWeight: 700 }}>Producto</TableCell>
                              <TableCell sx={{ fontWeight: 700 }}>Categoría</TableCell>
                              <TableCell align="right" sx={{ fontWeight: 700 }}>Veces</TableCell>
                              <TableCell sx={{ fontWeight: 700 }}>Proveedor</TableCell>
                              <TableCell align="right" sx={{ fontWeight: 700 }}>Costo</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {(recDetailData.products || [])
                              .filter((p: any) => {
                                if (!recDetailSearch) return true;
                                const q = recDetailSearch.toLowerCase();
                                return p.sku.toLowerCase().includes(q) || p.name.toLowerCase().includes(q);
                              })
                              .map((p: any) => (
                                <React.Fragment key={p.sku}>
                                  <TableRow sx={{ '& > *': { borderBottom: expandedRows[p.sku] ? 'unset' : undefined } }}>
                                    <TableCell sx={{ py: 0.5 }}>
                                      <IconButton
                                        size="small"
                                        onClick={() => setExpandedRows((prev) => ({ ...prev, [p.sku]: !prev[p.sku] }))}
                                      >
                                        {expandedRows[p.sku] ? <KeyboardArrowUpIcon fontSize="small" /> : <KeyboardArrowDownIcon fontSize="small" />}
                                      </IconButton>
                                    </TableCell>
                                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{p.sku}</TableCell>
                                    <TableCell sx={{ maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.name}</TableCell>
                                    <TableCell>{p.category || '-'}</TableCell>
                                    <TableCell align="right" sx={{ fontWeight: 700 }}>{p.occurrences}</TableCell>
                                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{p.supplier || '-'}</TableCell>
                                    <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>${p.cost?.toLocaleString('es-CL')}</TableCell>
                                  </TableRow>
                                  <TableRow>
                                    <TableCell sx={{ py: 0, px: 0 }} colSpan={7}>
                                      <Collapse in={expandedRows[p.sku]} timeout="auto" unmountOnExit>
                                        <Box sx={{ px: 3, py: 1.5, bgcolor: 'action.hover', borderRadius: 1, mx: 1, mb: 1 }}>
                                          <Typography variant="caption" sx={{ fontWeight: 700, mb: 1, display: 'block' }}>
                                            Historial de bloques ({(p.blocks || []).length})
                                          </Typography>
                                          <Table size="small">
                                            <TableHead>
                                              <TableRow>
                                                <TableCell sx={{ fontWeight: 600, fontSize: 12, py: 0.5 }}>Inicio</TableCell>
                                                <TableCell sx={{ fontWeight: 600, fontSize: 12, py: 0.5 }}>Fin</TableCell>
                                                <TableCell align="right" sx={{ fontWeight: 600, fontSize: 12, py: 0.5 }}>Días en alerta</TableCell>
                                                <TableCell align="right" sx={{ fontWeight: 600, fontSize: 12, py: 0.5 }}>Días fuera antes</TableCell>
                                              </TableRow>
                                            </TableHead>
                                            <TableBody>
                                              {(p.blocks || []).map((b: any, i: number) => (
                                                <TableRow key={i}>
                                                  <TableCell sx={{ fontSize: 12, py: 0.3 }}>{fmtDate(b.start)}</TableCell>
                                                  <TableCell sx={{ fontSize: 12, py: 0.3 }}>{fmtDate(b.end)}</TableCell>
                                                  <TableCell align="right" sx={{ fontSize: 12, py: 0.3, fontWeight: 600 }}>{b.days_in_alert}</TableCell>
                                                  <TableCell align="right" sx={{ fontSize: 12, py: 0.3, color: b.days_outside_before != null ? '#4caf50' : 'text.secondary' }}>
                                                    {b.days_outside_before != null ? `${b.days_outside_before}d` : '-'}
                                                  </TableCell>
                                                </TableRow>
                                              ))}
                                            </TableBody>
                                          </Table>
                                        </Box>
                                      </Collapse>
                                    </TableCell>
                                  </TableRow>
                                </React.Fragment>
                              ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </>
                  ) : (
                    <Typography color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>No se pudieron cargar los datos</Typography>
                  )}
                </DialogContent>
              </Dialog>

              {/* Modal lista completa de productos recurrentes */}
              <Dialog open={recFullListOpen} onClose={() => setRecFullListOpen(false)} maxWidth="lg" fullWidth>
                <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Box>
                    <Typography sx={{ fontWeight: 700, fontSize: 18 }}>Productos Más Recurrentes - Lista Completa</Typography>
                    {recData && (
                      <Typography variant="caption" color="text.secondary">{recData.total?.toLocaleString('es-CL')} productos</Typography>
                    )}
                  </Box>
                  <IconButton onClick={() => setRecFullListOpen(false)} size="small"><CloseIcon /></IconButton>
                </DialogTitle>
                <DialogContent>
                  <Box sx={{ display: 'flex', gap: 2, mb: 2, alignItems: 'center' }}>
                    <TextField
                      size="small"
                      placeholder="Buscar por SKU o nombre..."
                      sx={{ flex: 1 }}
                      value={recFullListSearch}
                      onChange={(e) => setRecFullListSearch(e.target.value)}
                    />
                    <Typography
                      sx={{ fontSize: 13, fontWeight: 600, color: 'primary.main', cursor: 'pointer', whiteSpace: 'nowrap', '&:hover': { textDecoration: 'underline' } }}
                      onClick={() => setRecFullListAsc(!recFullListAsc)}
                    >
                      Orden: {recFullListAsc ? '↑ Menor a mayor' : '↓ Mayor a menor'}
                    </Typography>
                  </Box>
                  <TableContainer sx={{ maxHeight: 500 }} key={String(recFullListAsc)}>
                    <Table size="small" stickyHeader>
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 700 }}>SKU</TableCell>
                          <TableCell sx={{ fontWeight: 700 }}>Producto</TableCell>
                          <TableCell sx={{ fontWeight: 700 }}>Métrica</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 700 }}>Veces</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 700 }}>Primera vez</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 700 }}>Última vez</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 700 }}>Prom. días</TableCell>
                          <TableCell sx={{ fontWeight: 700 }}>Proveedor</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {[...(recData?.products || [])]
                          .filter((p: any) => {
                            if (!recFullListSearch) return true;
                            const q = recFullListSearch.toLowerCase();
                            return p.sku.toLowerCase().includes(q) || p.name.toLowerCase().includes(q);
                          })
                          .sort((a: any, b: any) => recFullListAsc ? a.occurrences - b.occurrences : b.occurrences - a.occurrences)
                          .map((p: any) => (
                            <TableRow key={p.sku + p.metric_key} hover>
                              <TableCell sx={{ whiteSpace: 'nowrap' }}>{p.sku}</TableCell>
                              <TableCell sx={{ maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.name}</TableCell>
                              <TableCell sx={{ whiteSpace: 'nowrap' }}>{p.metric}</TableCell>
                              <TableCell align="right" sx={{ fontWeight: 700, color: '#f44336' }}>{p.occurrences}</TableCell>
                              <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>{fmtDate(p.first_occurrence)}</TableCell>
                              <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>{fmtDate(p.last_occurrence)}</TableCell>
                              <TableCell align="right">{p.avg_days_in_alert?.toFixed(1)}</TableCell>
                              <TableCell sx={{ whiteSpace: 'nowrap' }}>{p.supplier || '-'}</TableCell>
                            </TableRow>
                          ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </DialogContent>
              </Dialog>
            </CustomTabPanel>
          </>
        </div>
      }
      scroll={isMobile ? 'normal' : 'content'}
      ref={pageLayout}
    />
  );
};

export default CompanyMetrics;
